<template>
  <!-- Banner Carousel (when API returned images) -->
  <div
    v-if="slides.length > 0"
    id="heroCarousel"
    class="carousel slide d-flex align-items-center p-0"
    data-bs-ride="carousel"
    role="region"
    aria-roledescription="carrusel"
    aria-label="Carrusel de promociones"
    aria-live="off"
  >
    <!-- Carousel indicators -->
    <div class="carousel-indicators pt-0 mt-0">
      <button 
        v-for="(slide, index) in slides" 
        :key="slide.id" 
        type="button" 
        data-bs-target="#heroCarousel" 
        :data-bs-slide-to="index" 
        :class="{ active: index === activeSlide }" 
        :aria-current="index === activeSlide ? 'true' : 'false'"
        aria-controls="heroCarousel"
        :aria-label="slideButtonLabel(slide, index)">
      </button>
    </div>
    
    <!-- Carousel items -->
    <div class="carousel-inner h-100">
      <div 
        v-for="(slide, index) in slides" 
        :key="slide.id" 
        class="carousel-item h-100" 
        :class="{ active: index === activeSlide }">
        
        <a
          :href="slide.link"
          class="w-100 h-100 d-flex align-items-center justify-content-center carousel-slide-link"
          :aria-label="slideLinkLabel(slide, index)"
        >
          <img 
            :src="heroSlideImg(slide, index)" 
            :srcset="heroSlideSrcset(slide, index) || undefined"
            sizes="100vw"
            :alt="slideAltText(slide, index)" 
            class="slide-image w-100" 
            :fetchpriority="index === 0 ? 'high' : 'low'"
            :loading="index === 0 ? 'eager' : 'lazy'"
            decoding="async"
            width="1920"
            height="1080"
          />
        </a>
      </div>
    </div>
  </div>

  <!-- Fallback hero: banner-fallback.webp (visible immediately, avoids NO_LCP while carousel API loads) -->
  <section v-else class="hero-fallback d-flex align-items-center p-0" aria-label="Banner principal">
    <a
      href="/productos"
      class="w-100 h-100 d-flex align-items-center justify-content-center carousel-slide-link"
      aria-label="Ver catálogo"
    >
      <img
        :src="bannerFallbackSrc"
        alt="Semillero del Oeste - Las mejores semillas para tu cultivo"
        class="slide-image w-100"
        fetchpriority="high"
        loading="eager"
        decoding="async"
        width="1920"
        height="1080"
      />
    </a>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { getPublicCarouselImages, getCarouselStatus } from '@/services/uploads';
import { optimizeCloudinaryUrl, cloudinarySrcset } from '@/utils/cloudinaryImage';
import bannerFallbackMobile from '@/assets/images/banner-fallback-mobile.webp';

const FORCE_SHOW_BANNERS = false;

const slides = ref([]);
const isMobile = ref(window.innerWidth <= 768);
const carouselEnabled = ref(true);
const activeSlide = ref(0);
let carouselRoot = null;
let carouselSlideHandler = null;

// Imagen de fallback del hero: se muestra de inmediato mientras carga la API
// de banners (evita NO_LCP) y cuando no hay banners configurados.
// Se sirve desde `public/lcp/` para que coincida con el preload de index.html.
const bannerFallback = `${String(process.env.BASE_URL || '/').replace(/\/$/, '')}/lcp/banner-fallback.webp`;

// En mobile se usa el fallback específico de assets/images
const bannerFallbackSrc = computed(() =>
  isMobile.value ? bannerFallbackMobile : bannerFallback
);

const heroSlideRaw = (slide) => {
  if (isMobile.value && slide.mobileImage) return slide.mobileImage;
  return slide.image;
};

const heroSlideImg = (slide, index) =>
  optimizeCloudinaryUrl(heroSlideRaw(slide), {
    maxWidth: isMobile.value ? 960 : 1920,
    quality: index === 0 ? 'good' : 'eco',
  });

const heroSlideSrcset = (slide, index) =>
  cloudinarySrcset(heroSlideRaw(slide), [480, 800, 1200, 1600, 1920], {
    quality: index === 0 ? 'good' : 'eco',
  });

const slideTitle = (slide) => {
  return slide?.title || slide?.name || slide?.alt_text || slide?.alt || slide?.heading || '';
};

const slideButtonLabel = (slide, index) => {
  const title = slideTitle(slide);
  return title ? `Ir al banner: ${title}` : `Ir al banner ${index + 1}`;
};

const slideLinkLabel = (slide, index) => {
  const title = slideTitle(slide);
  return title ? `Ver banner: ${title}` : `Ver banner ${index + 1}`;
};

const slideAltText = (slide, index) => {
  const title = slideTitle(slide);
  return title ? `Banner ${title}` : `Banner ${index + 1}`;
};

// Actualizar isMobile en resize
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

// Inicializar Bootstrap Carousel
const initCarousel = () => {
  nextTick(() => {
    const carouselElement = document.getElementById('heroCarousel');
    if (carouselElement && window.bootstrap) {
      // Destruir instancia previa si existe
      const existingCarousel = window.bootstrap.Carousel.getInstance(carouselElement);
      if (existingCarousel) {
        existingCarousel.dispose();
      }
      // Crear nueva instancia
      new window.bootstrap.Carousel(carouselElement, {
        interval: 5000,
        wrap: true,
        keyboard: true,
        pause: 'hover'
      });

      if (carouselSlideHandler) {
        carouselElement.removeEventListener('slid.bs.carousel', carouselSlideHandler);
      }
      carouselSlideHandler = (event) => {
        if (typeof event?.to === 'number') {
          activeSlide.value = event.to;
        }
      };
      carouselElement.addEventListener('slid.bs.carousel', carouselSlideHandler);
      carouselRoot = carouselElement;
      activeSlide.value = 0;
    }
  });
};

const fetchCarouselImages = async () => {
  try {
    
    // Si FORCE_SHOW_BANNERS está activo, ignorar el estado de la DB
    if (!FORCE_SHOW_BANNERS) {
      // Primero verificar si el carousel de banners está habilitado
      const statusData = await getCarouselStatus();
      carouselEnabled.value = statusData.carousel_enabled;
    } else {
      carouselEnabled.value = true;
    }
    
    // Solo cargar imágenes si el carousel está habilitado
    if (carouselEnabled.value) {
      const data = await getPublicCarouselImages();
      if (data && data.length > 0) {
        slides.value = data;
      }
    }
  } catch (error) {
    console.error('Error loading carousel images:', error);
    // Si falla, slides.value quedará vacío y se usará staticSlides
  } finally {
    if (slides.value.length > 0) {
      initCarousel();
    }
  }
};

onMounted(() => {
  fetchCarouselImages();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  if (carouselRoot && carouselSlideHandler) {
    carouselRoot.removeEventListener('slid.bs.carousel', carouselSlideHandler);
  }
});
</script>

<style scoped>
/* Carousel container */
#heroCarousel,
.hero-fallback {
  height: 100vh;
  max-height: 100vh;
  min-height: 500px;
  position: relative;
  overflow: hidden;
  padding-top: 80px;
  width: 100%;
}

.carousel-item {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-slide-link {
  position: relative;
}

.carousel-slide-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.7) 70%, #000 100%);
  pointer-events: none;
  z-index: 1;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* No fadeIn animation: image must be visible immediately for LCP detection */
}

/* Content alignment and styling */
.slide-text-wrapper {
  padding: 2rem 0;
}

.text-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Text styling */
.slide-title {
  font-size: 5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffffff;
  text-align: left;
  animation: slideFromLeft 0.8s forwards;
}

.slide-description {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.6;
  animation: slideFromLeft 1s forwards;
  animation-delay: 0.2s;
  opacity: 0;
}

.slide-button {
  display: inline-block;
  padding: 0.75rem 2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  animation: slideFromLeft 1.2s forwards;
  animation-delay: 0.4s;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Bootstrap carousel indicator styling */
.carousel-indicators {
  position: absolute;
  bottom: 20px;
  z-index: 15;
  display: flex;
  justify-content: center;
  gap: 4px;
}

.carousel-indicators button {
  width: 44px;
  height: 44px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.carousel-indicators button::before {
  content: '';
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.75);
  transition: transform 0.3s ease, opacity 0.3s ease;
  will-change: transform;
}

.carousel-indicators button.active::before {
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95);
  transform: scale(1.3);
}

/* Animation keyframes */
@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive styles */
@media (max-width: 991.98px) {
  #heroCarousel {
    height: 100vh !important;
    max-height: unset;
    padding-top: 60px;
    padding-bottom: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
  }
  
  .carousel-item {
    height: 100%;
  }
  
  .carousel-slide-link {
    height: 100%;
  }
  
  .carousel-slide-link::after {
    display: block;
    height: 50%;
  }
  
  .slide-image {
    height: 100%;
    max-height: unset;
    object-fit: cover;
    width: 100%;
  }
  
  .slide-title {
    font-size: 2.5rem;
    text-align: center;
  }
  
  .slide-description {
    text-align: center;
  }
  
  .slide-text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem 1rem;
  }
  
  .carousel-item .row {
    flex-direction: column-reverse;
  }
}

@media (max-width: 767.98px) {
  #heroCarousel {
    padding-top: 60px;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    overflow-y: auto;
  }

  .carousel-slide-link::after {
    display: block;
    height: 40%;
    background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.8) 60%, #000 100%);
  }

  .slide-image {
    height: 100%;
    max-height: unset;
    width: 100%;
    object-fit: cover;
  }
  
  .slide-title {
    font-size: 2rem;
  }
  
  .slide-description {
    font-size: 1rem;
  }
  
  .carousel-indicators{
    bottom: 10px;
    z-index: 20;
  }
}

/* ============================================
   FALLBACK HERO STYLES (Original Hero Design)
   ============================================ */
.hero {
  min-height: 100vh;
  position: relative;
  background-color: var(--black-color);
  overflow: hidden;
}

.hero-wrapper {
  width: 100%;
  padding: 50px 0;
  position: relative;
}

.hero-images-container {
  min-height: 450px;
  contain: layout;
  /* Evita salto cuando en móvil el carrusel mide 300px de alto */
  overflow: hidden;
}

.hero-carousel-wrapper {
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 2rem;
  margin: -2rem;
}

.hero-text {
  position: relative;
  z-index: 3;
  /* No fadeIn: h1 must be visible on first paint for LCP/FCP */
}

.gradient_title {
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--tertiary-color)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-carousel {
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 2rem 0;
}

.carousel-container {
  position: relative;
  height: 450px;
  width: 100%;
}

.carousel-slide {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0.8);
  pointer-events: none;
}

.carousel-slide.active {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
}

.carousel-slide.prev {
  transform: translateX(-100%) scale(0.8);
  opacity: 0;
}

.carousel-slide.next {
  transform: translateX(100%) scale(0.8);
  opacity: 0;
}

.hero-images-container .hero-image {
  max-height: 450px;
  filter: drop-shadow(0 0px 2rem rgba(0, 137, 16, 0));
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-images-container .carousel-slide.active .hero-image {
  filter: drop-shadow(20px 0px 10rem rgba(110, 0, 137, 0.185));
}

.hero-images-container .hero-image:hover {
  cursor: pointer;
  transform: scale(1.03);
  transition: 0.4s all ease;
}

@media (max-width: 991.98px) {
  .hero-wrapper {
    padding: 80px 0 30px;
  }

  .hero-images-container {
    min-height: 300px;
  }

  .hero-carousel-wrapper {
    padding: 1rem;
    margin: -1rem;
  }

  .carousel-container {
    height: 300px;
  }

  .hero-images-container .hero-image {
    width: 100%;
    max-height: 300px;
    object-fit: contain;
  }
}

@media (max-width: 767.98px) {
  .hero-wrapper {
    padding: 100px 0 20px;
  }
}
</style>
